import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { LegalOpinionRequest } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, PageHeader, Spinner, StatusBadge } from '../../ui/components';

/**
 * GET /legal-opinions/:id — LEGISLATIVE_DRAFTER ("AG Chambers counsel"), or
 * the requesting officer. issue() computes opinionSha256 locally and
 * deliberately never sends the opinion text to DocVault/OpenGov — legal
 * advice to Government carries legal professional privilege (see
 * m38-attorney-general/src/routes/legal-opinions.ts).
 */
export function LegalOpinionDetail() {
  const { id } = useParams<{ id: string }>();
  const { persona } = useAuth();
  const isCounsel = persona?.roles.includes('LEGISLATIVE_DRAFTER') ?? false;
  const opinionQ = useApiQuery<LegalOpinionRequest>(id ? `/api/v1/m38/legal-opinions/${id}` : null);

  const [assignNin, setAssignNin] = useState('');
  const [opinionText, setOpinionText] = useState('');
  const [withdrawReason, setWithdrawReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const act = async (path: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m38/legal-opinions/${id}/${path}`, body);
      opinionQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  if (opinionQ.loading) return <Spinner label="Loading opinion request…" />;
  if (opinionQ.error) return <ErrorNote message={opinionQ.error} />;
  const o = opinionQ.data;
  if (!o) return null;

  return (
    <div>
      <PageHeader title={o.subject} subtitle={`${o.requestingMda} — requested by ${o.requestedBy} · ${o.urgency}`} actions={<StatusBadge status={o.status} />} />

      {error ? (
        <div className="mb-4">
          <ErrorNote message={error} />
        </div>
      ) : null}

      <div className="mb-5 card p-5">
        <h2 className="mb-2 text-sm font-bold text-slate-700">Question</h2>
        <p className="whitespace-pre-wrap text-sm text-slate-700">{o.question}</p>
      </div>

      {o.opinionText ? (
        <div className="mb-5 card border-l-4 border-l-brand-500 p-5">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-700">Opinion</h2>
            <span className="text-xs text-slate-400">{o.issuedAt ? formatDateTime(o.issuedAt) : ''}</span>
          </div>
          <p className="whitespace-pre-wrap text-sm text-slate-700">{o.opinionText}</p>
          {o.opinionSha256 ? (
            <p className="mt-2 text-[11px] text-slate-400">
              sha256 <code>{o.opinionSha256}</code> — kept locally only (legal professional privilege), never issued to DocVault.
            </p>
          ) : null}
        </div>
      ) : null}

      {o.withdrawnReason ? (
        <div className="mb-5">
          <ErrorNote message={`Withdrawn: ${o.withdrawnReason}`} />
        </div>
      ) : null}

      <div className="space-y-3">
        {isCounsel && o.status === 'REQUESTED' ? (
          <div className="card flex flex-wrap items-end gap-2 p-4">
            <Field label="Assign to counsel (NIN)" className="!mb-0">
              <input className="field-input" value={assignNin} onChange={(e) => setAssignNin(e.target.value)} />
            </Field>
            <button className="btn-primary" onClick={() => act('assign', { assignedCounselNin: assignNin })} disabled={busy || !assignNin.trim()}>
              Assign
            </button>
          </div>
        ) : null}

        {isCounsel && o.status === 'IN_REVIEW' ? (
          <div className="card space-y-2 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Issue opinion</div>
            <textarea className="field-input" rows={6} placeholder="Opinion text…" value={opinionText} onChange={(e) => setOpinionText(e.target.value)} />
            <button className="btn-primary" onClick={() => act('issue', { opinionText })} disabled={busy || !opinionText.trim()}>
              Issue opinion
            </button>
          </div>
        ) : null}

        {(o.status === 'REQUESTED' || o.status === 'IN_REVIEW') ? (
          <div className="card flex flex-wrap items-end gap-2 p-4">
            <Field label="Withdraw — reason" className="min-w-[220px] flex-1">
              <input className="field-input" value={withdrawReason} onChange={(e) => setWithdrawReason(e.target.value)} />
            </Field>
            <button className="btn-danger" onClick={() => act('withdraw', { reason: withdrawReason })} disabled={busy || withdrawReason.trim().length < 3}>
              Withdraw
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
